import { useEffect, useRef, useState } from 'react';
import { AppLink } from '@/shared/components/AppLink';
import { EllipsisVertical, ExternalLink, Minus, Plus } from 'lucide-react';

import { Logo } from '@/shared/icons/logo';
import Search from '@/features/search/presentation/components/Search';
import AccountDropdown from './AccountDropdown';
import { headerLayoutWrapperClass, useDesktopHeaderConfig } from '@/shared/features/header/presentation/hooks/useDesktopHeaderConfig';
import { useTrackHeaderCtaClick } from '@/shared/features/header/presentation/hooks/useTrackHeaderCtaClick';
import {
  HEADER_CTA_CLICKED,
  headerCtaClickedEventProperties,
} from '@/shared/features/analytics/utils/headerCtaClicked';
import footerSectionsData from '@/shared/features/footer/presentation/data/footerSections.json';

interface HeaderDesktopProps {
  cartCount: number;
  wishlistCount: number;
  isLoggedIn: boolean;
  isSoftLoggedIn?: boolean;
  userName?: string;
  isUserContextLoading?: boolean;
  onSearch: (query: string) => void;
  onOpenLogin?: (mode?: 'login' | 'signup') => void;
}

interface FooterItem {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  items: FooterItem[];
  isLink?: boolean;
}

const footerSections: FooterSection[] = footerSectionsData as FooterSection[];

function normalizeFooterTitle(raw: string): string {
  return raw
    .toLowerCase()
    .split(' ')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href) || href.startsWith('mailto:') || href.startsWith('tel:');
}

function RecentlyViewedIcon({ color }: { color: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6 shrink-0" fill="none" style={{ color }}>
      <path d="M21.2498 12.0005C21.2498 17.1095 17.1088 21.2505 11.9998 21.2505C6.89076 21.2505 2.74976 17.1095 2.74976 12.0005C2.74976 6.8915 6.89076 2.7505 11.9998 2.7505C17.1088 2.7505 21.2498 6.8915 21.2498 12.0005Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.4314 14.9429L11.6614 12.6939V7.8469" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function VideoCallIcon({ color }: { color: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6 shrink-0" fill="none" style={{ color }}>
      <path d="M16.2969 15.5383C16.3778 17.3704 14.8992 18.9196 12.9946 18.9975C12.8543 19.0034 6.01531 18.9896 6.01531 18.9896C4.11996 19.1335 2.46115 17.7715 2.31165 15.9463C2.30039 15.8103 2.30346 8.47219 2.30346 8.47219C2.21949 6.63815 3.69604 5.08499 5.60163 5.00418C5.74396 4.99728 12.5738 5.01009 12.5738 5.01009C14.4783 4.86818 16.1423 6.24001 16.2897 8.07405C16.3 8.2061 16.2969 15.5383 16.2969 15.5383Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16.3 9.97984L19.593 7.28484C20.409 6.61684 21.633 7.19884 21.632 8.25184L21.62 15.6008C21.619 16.6538 20.394 17.2308 19.58 16.5628L16.3 13.8678" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StoreLocationIcon({ color }: { color: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6 shrink-0" fill="none" style={{ color }}>
      <path d="M14.5 10.5005C14.5 9.11924 13.3808 8 12.0005 8C10.6192 8 9.5 9.11924 9.5 10.5005C9.5 11.8808 10.6192 13 12.0005 13C13.3808 13 14.5 11.8808 14.5 10.5005Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.9995 21C10.801 21 4.5 15.8984 4.5 10.5633C4.5 6.38664 7.8571 3 11.9995 3C16.1419 3 19.5 6.38664 19.5 10.5633C19.5 15.8984 13.198 21 11.9995 21Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WishlistIcon({ color }: { color: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6 shrink-0" fill="none" style={{ color }}>
      <path d="M2.87187 11.5983C1.79887 8.24832 3.05287 4.41932 6.56987 3.28632C8.41987 2.68932 10.4619 3.04132 11.9999 4.19832C13.4549 3.07332 15.5719 2.69332 17.4199 3.28632C20.9369 4.41932 22.1989 8.24832 21.1269 11.5983C19.4569 16.9083 11.9999 20.9983 11.9999 20.9983C11.9999 20.9983 4.59787 16.9703 2.87187 11.5983Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 6.7C17.07 7.046 17.826 8.001 17.917 9.122" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CartBagIcon({ color }: { color: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6 shrink-0" fill="none" style={{ color }}>
      <path d="M16.5136 21.5H8.16579C5.09943 21.5 2.74703 20.3924 3.41522 15.9348L4.19325 9.89359C4.60515 7.66934 6.02392 6.81808 7.26877 6.81808H17.4473C18.7104 6.81808 20.0468 7.73341 20.5228 9.89359L21.3008 15.9348C21.8683 19.889 19.58 21.5 16.5136 21.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16.651 6.5984C16.651 4.21233 14.7167 2.27803 12.3306 2.27803V2.27803C11.1816 2.27316 10.078 2.72619 9.26381 3.53695C8.44963 4.3477 7.99193 5.44939 7.99194 6.5984H7.99194" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.2962 11.1018H15.2505" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.46566 11.1018H9.41989" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function utilityLabelClassName(): string {
  return 'whitespace-nowrap text-center text-[10px] font-semibold leading-none sm:text-[11px]';
}

function UtilitySeparator({ gradientColor, className }: { gradientColor: string, className?: string }) {
  return (
    <span
      aria-hidden
      className={`h-10 shrink-0 self-center opacity-20 ${className}`}
      style={{
        width: 1,
        background: `linear-gradient(to bottom, transparent 0%, ${gradientColor} 45%, ${gradientColor} 55%, transparent 100%)`,
      }}
    />
  );
}

export default function HeaderDesktop({
  cartCount,
  wishlistCount,
  isLoggedIn,
  isSoftLoggedIn = false,
  userName,
  onSearch,
  onOpenLogin,
}: HeaderDesktopProps) {
  const desktop = useDesktopHeaderConfig();
  const trackHeaderCtaClick = useTrackHeaderCtaClick();
  const layoutClass = headerLayoutWrapperClass(desktop.layout);
  const labelColor = { color: desktop.headerTextColor } as const;
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [openFooterSection, setOpenFooterSection] = useState<string | null>(
    footerSections.find((section) => !section.isLink)?.title ?? null
  );
  const moreMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMoreOpen) return;

    const onDocPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (moreMenuRef.current && !moreMenuRef.current.contains(target)) {
        setIsMoreOpen(false);
      }
    };

    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMoreOpen(false);
    };

    document.addEventListener('pointerdown', onDocPointerDown);
    document.addEventListener('keydown', onEsc);

    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [isMoreOpen]);

  return (
    <div className="overflow-visible border-b" style={{ backgroundColor: desktop.topBarBg, borderBottomColor: desktop.headerBottomBorderColor }}>
      <div className={`${layoutClass} overflow-visible py-2`} style={{ backgroundColor: desktop.mainBarBg }}>
        <div className="flex items-center justify-between gap-4 overflow-visible md:gap-6">
          {/* Logo */}
          <div className="logo relative min-w-0 shrink-0">
            <AppLink
              to="/"
              eventName="BlueStone Logo Clicked"
              className="flex items-center transition-opacity hover:opacity-80 min-w-[40px]"
              aria-label="BlueStone Logo"
              style={labelColor}
            >
              <Logo showLink={false} className="h-auto w-full max-w-[180px] md:max-w-[280px]" />
            </AppLink>
            {/* Covers the BS symbol in the logo so it appears to fly out during the scroll animation */}
            <div
              className="pointer-events-none absolute left-0 top-0 h-full aspect-square"
              style={{ backgroundColor: desktop.topBarBg }}
              aria-hidden
            />
          </div>

          {/* Search */}
          <Search
            onSearch={onSearch}
            visualVariant="pill"
            className="min-w-0"
            pillTheme={{
              bgColor: desktop.searchBg,
              borderColor: desktop.searchBorderColor,
              textColor: desktop.searchTextColor,
              placeholderColor: desktop.searchPlaceholderColor,
              iconColor: desktop.searchIconColor,
            }}
          />

          {/* Utilities */}
          <div className="flex shrink-0 items-center justify-end gap-0">
            <AppLink
              to="/recently-viewed"
              eventName={HEADER_CTA_CLICKED}
              eventProperties={headerCtaClickedEventProperties('Recently viewed')}
              className="group flex shrink-0 flex-col items-center gap-1.5 px-2 min-w-[40px] transition-opacity hover:opacity-75 min-[1100px]:px-2.5 min-[1200px]:px-3"
              style={labelColor}
            >
              <RecentlyViewedIcon color={desktop.headerTextColor} />
              <span className={utilityLabelClassName()}>Recently viewed</span>
            </AppLink>

            <UtilitySeparator gradientColor={desktop.navbarLinkColor} />

            <AppLink
              to="/tah?cartType=videoCallTah"
              shouldRefresh
              eventName={HEADER_CTA_CLICKED}
              eventProperties={headerCtaClickedEventProperties('Video call cart')}
              className="group flex shrink-0 flex-col items-center gap-1.5 px-2 min-w-[40px] transition-opacity hover:opacity-75 min-[1100px]:px-2.5 min-[1200px]:px-3"
              style={labelColor}
            >
              <VideoCallIcon color={desktop.headerTextColor} />
              <span className={utilityLabelClassName()}>Video call cart</span>
            </AppLink>

            <UtilitySeparator gradientColor={desktop.navbarLinkColor} />

            <AppLink
              to="/store.html"
              eventName={HEADER_CTA_CLICKED}
              eventProperties={headerCtaClickedEventProperties('Find a store')}
              className="group flex shrink-0 flex-col items-center gap-1.5 px-2 min-w-[40px] transition-opacity hover:opacity-75 min-[1100px]:px-2.5 min-[1200px]:px-3"
              style={labelColor}
            >
              <StoreLocationIcon color={desktop.headerTextColor} />
              <span className={utilityLabelClassName()}>Find a store</span>
            </AppLink>

            <UtilitySeparator gradientColor={desktop.navbarLinkColor} />

            <AppLink
              to="/wishlist/view"
              shouldRefresh
              eventName={HEADER_CTA_CLICKED}
              eventProperties={headerCtaClickedEventProperties('Wishlist')}
              className="group relative flex shrink-0 flex-col items-center gap-1.5 px-2 min-w-[40px] transition-opacity hover:opacity-75 min-[1100px]:px-2.5 min-[1200px]:px-3"
              style={labelColor}
              onClick={(e) => {
                if (!isLoggedIn && !isSoftLoggedIn && onOpenLogin) {
                  e.preventDefault();
                  onOpenLogin('login');
                }
              }}
            >
              <span className="relative inline-flex">
                <WishlistIcon color={desktop.headerTextColor} />
                {wishlistCount > 0 ? (
                  <span
                    className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-md border bg-white px-1 text-[10px] font-semibold tabular-nums"
                    style={{ borderColor: desktop.headerTextColor, color: desktop.headerTextColor }}
                  >
                    {wishlistCount}
                  </span>
                ) : null}
              </span>
              <span className={utilityLabelClassName()}>Wishlist</span>
            </AppLink>

            <UtilitySeparator gradientColor={desktop.navbarLinkColor} />

            <AppLink
              to="/order"
              shouldRefresh
              eventName={HEADER_CTA_CLICKED}
              eventProperties={headerCtaClickedEventProperties('Cart')}
              className="group relative flex min-w-[40px] flex-col items-center gap-1.5 pr-0.5 transition-opacity hover:opacity-75 min-[1200px]:w-[58px]"
              style={labelColor}
            >
              <span className="relative inline-flex">
                <CartBagIcon color={desktop.headerTextColor} />
                {cartCount > 0 ? (
                  <span
                    className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-md border bg-white px-1 text-[10px] font-semibold tabular-nums"
                    style={{ borderColor: desktop.headerTextColor, color: desktop.headerTextColor }}
                  >
                    {cartCount}
                  </span>
                ) : null}
              </span>
              <span className={utilityLabelClassName()}>Cart</span>
            </AppLink>

            <UtilitySeparator gradientColor={desktop.navbarLinkColor} />

            <AccountDropdown
              userName={userName}
              presentation="headerProfile"
              accentColor={desktop.headerTextColor}
              isLoggedIn={isLoggedIn}
              onOpenLogin={onOpenLogin}
            />

            <UtilitySeparator gradientColor={desktop.navbarLinkColor} className="ml-1" />

            <div ref={moreMenuRef} className="relative ml-1">
              <button
                type="button"
                onClick={() => {
                  trackHeaderCtaClick('More');
                  setIsMoreOpen((prev) => !prev);
                }}
                className="group flex min-w-[40px] flex-col items-center gap-1.5 px-2 transition-opacity hover:opacity-75 min-[1100px]:px-2.5 min-[1200px]:px-3"
                style={labelColor}
                aria-haspopup="dialog"
                aria-expanded={isMoreOpen}
                aria-label="Open more links"
              >
                <span
                  className="rounded-md transition-colors"
                  style={{
                    backgroundColor: isMoreOpen ? desktop.headerTextColor : 'transparent',
                    color: isMoreOpen ? '#ffffff' : desktop.headerTextColor,
                  }}
                >
                  <EllipsisVertical className="h-6 w-6" />
                </span>
                <span className={utilityLabelClassName()}>More</span>
              </button>

              {isMoreOpen && (
                <div className="absolute right-0 top-[calc(100%+10px)] z-[90] w-[280px] rounded-xl p-0 shadow-[0_14px_28px_rgba(0,0,0,0.28)]">
                  <div className="rounded-b-2xl bg-white">
                    {footerSections.map((section) => {
                      const sectionTitle = normalizeFooterTitle(section.title);
                      const isLinkSection = Boolean(section.isLink);
                      const sectionOpen = openFooterSection === section.title;

                      if (isLinkSection) {
                        const onlyItem = section.items[0];
                        const href = onlyItem?.href ?? '#';
                        return (
                          <a
                            key={section.title}
                            href={href}
                            target={isExternalHref(href) ? '_blank' : undefined}
                            rel={isExternalHref(href) ? 'noopener noreferrer' : undefined}
                            className="flex items-center justify-between border-t border-gray-100 px-6 py-5 text-xs text-[#0c2540] transition-colors hover:bg-gray-50"
                            onClick={() => {
                              trackHeaderCtaClick(onlyItem?.label ?? sectionTitle);
                              setIsMoreOpen(false);
                            }}
                          >
                            <span>{onlyItem?.label ?? sectionTitle}</span>
                            <ExternalLink className="h-5 w-5 text-[#122f4b]" />
                          </a>
                        );
                      }

                      return (
                        <div key={section.title} className="border-t border-gray-100">
                          <button
                            type="button"
                            onClick={() => {
                              trackHeaderCtaClick(sectionTitle);
                              setOpenFooterSection((prev) => (prev === section.title ? null : section.title));
                            }}
                            className="flex w-full items-center justify-between px-6 py-5 text-left text-xs text-[#0c2540]"
                          >
                            <span>{sectionTitle}</span>
                            {sectionOpen ? (
                              <Minus className="h-5 w-5 text-[#122f4b]" />
                            ) : (
                              <Plus className="h-5 w-5 text-[#bec5cd]" />
                            )}
                          </button>

                          <div
                            className={[
                              'grid transition-[grid-template-rows,opacity] duration-300 ease-out',
                              sectionOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                            ].join(' ')}
                          >
                            <div className="overflow-hidden">
                              <div className="pb-4">
                                {section.items.map((item) => {
                                  const external = isExternalHref(item.href);
                                  return (
                                    <a
                                      key={`${section.title}-${item.label}`}
                                      href={item.href}
                                      target={external ? '_blank' : undefined}
                                      rel={external ? 'noopener noreferrer' : undefined}
                                      className="flex items-center justify-between px-6 py-3 text-xs text-[#0c2540] transition-colors hover:bg-gray-50"
                                      onClick={() => {
                                        trackHeaderCtaClick(item.label);
                                        setIsMoreOpen(false);
                                      }}
                                    >
                                      <span className="whitespace-pre-line">{item.label}</span>
                                      <ExternalLink className="h-4 w-4 text-[#122f4b]" />
                                    </a>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
